// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/services/entregaService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import { paramsDaLista, type ListaFiltros, type PaginadoComResumo, type EntregaListaResumo } from './listaFiltros';
import type {
  EntregaResumo,
  EntregaProduto,
  EntregaDetalhe,
  AtualizarStatusEntregaRequest,
  ConferirEntregaProdutoRequest,
  FecharConferenciaEntregaRequest,
} from '../types';
import { paramsConsulta, type FiltroConsultaOperacional } from '../consultas';

const BASE = '/api/entregas';

/** KPIs de GET /api/entregas/resumo — consulta inteira, sem o status. Valores excluem canceladas. */
export interface EntregaKpi {
  total: number;
  pendentes: number;
  emRota: number;
  entregues: number;
  canceladas: number;
  retiradas: number;
  valorTotal: number;
  valorPendente: number;
}

/** Filtros da consulta de entregas: busca, status, dtInicio/dtFim (data de entrega), clienteId, rotaId, motoristaId. */
export type FiltroEntregas = FiltroConsultaOperacional & { rotaId?: number; motoristaId?: number };

export const entregaService = {
  /**
   * Lista paginada + `resumo` (KPIs v118). Aceita os filtros da v118 (`decrescente`) e os da
   * paridade (`direcao`, `clienteId`, `rotaId`, `motoristaId`) — o servidor entende os dois.
   */
  async getAll(
    pagina = 1,
    tamanhoPagina = 20,
    filtros?: FiltroEntregas | ListaFiltros,
  ): Promise<PaginadoComResumo<EntregaResumo, EntregaListaResumo>> {
    const { data } = await apiClient.get<PaginadoComResumo<EntregaResumo, EntregaListaResumo>>(BASE, {
      params: { ...paramsDaLista(pagina, tamanhoPagina), ...paramsConsulta(filtros as FiltroEntregas) },
    });
    return data;
  },

  /** KPIs sobre o filtro inteiro (ignora status). */
  async getResumo(filtros?: FiltroEntregas): Promise<EntregaKpi> {
    const { status: _s, ordenarPor: _o, direcao: _d, ...resto } = filtros ?? {};
    const { data } = await apiClient.get<EntregaKpi>(`${BASE}/resumo`, { params: paramsConsulta(resto) });
    return data;
  },

  async getPendentes(): Promise<EntregaResumo[]> {
    const { data } = await apiClient.get<EntregaResumo[]>(`${BASE}/pendentes`);
    return data;
  },

  async getPorPeriodo(inicio: string, fim: string): Promise<EntregaResumo[]> {
    const { data } = await apiClient.get<EntregaResumo[]>(`${BASE}/periodo`, {
      params: { inicio, fim },
    });
    return data;
  },

  async getById(id: number): Promise<EntregaDetalhe> {
    const { data } = await apiClient.get<EntregaDetalhe>(`${BASE}/${id}`);
    return data;
  },

  async atualizarStatus(id: number, request: AtualizarStatusEntregaRequest): Promise<EntregaResumo> {
    const { data } = await apiClient.put<EntregaResumo>(`${BASE}/${id}/status`, request);
    return data;
  },

  async conferirProduto(
    id: number,
    itemId: number,
    request: ConferirEntregaProdutoRequest,
  ): Promise<EntregaProduto> {
    const { data } = await apiClient.put<EntregaProduto>(
      `${BASE}/${id}/produtos/${itemId}/conferir`,
      request,
    );
    return data;
  },

  async fecharConferencia(
    id: number,
    request: FecharConferenciaEntregaRequest = {},
  ): Promise<EntregaDetalhe> {
    const { data } = await apiClient.post<EntregaDetalhe>(`${BASE}/${id}/conferir`, request);
    return data;
  },

  async reabrirConferencia(id: number): Promise<EntregaResumo> {
    const { data } = await apiClient.put<EntregaResumo>(`${BASE}/${id}/reabrir-conferencia`, {});
    return data;
  },
};
